import React from 'react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  FileText, Briefcase, Building, PiggyBank, TrendingUp, Landmark,
  PoundSterling, Plus, Trash2, Ship, StickyNote, Wallet,
} from 'lucide-react';
import { CurrencyInput, Section, money } from './TaxReturnControls';

/**
 * Renders all SA100 income/expense sections on the left column of the Tax Return page.
 */
const IncomeSections = ({
  data,
  updateField,
  updateOtherIncome,
  addOtherIncome,
  removeOtherIncome,
}) => (
  <>
    {/* SA102 — Employment */}
    <Section title="SA102 — Employment Income" icon={Briefcase} iconColor="text-blue-400"
      badge={data.qualifying_days > 0 && (
        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px] ml-2">
          SED {data.qualifying_days}d
        </Badge>
      )}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <CurrencyInput label="Gross Employment Income" sublabel="Auto-filled from app trips"
          value={data.employment_income} onChange={v => updateField('employment_income', v)}
          icon={Ship} testId="employment-income" />
        <CurrencyInput label="Seafarer's Earnings Deduction" sublabel="HS205 — qualifying days abroad"
          value={data.sed_deduction} onChange={v => updateField('sed_deduction', v)}
          icon={Landmark} testId="sed-deduction" />
      </div>
      <div className="mt-2 flex items-center gap-3 text-[10px] text-slate-500">
        <span>Trips: {data.trips_count || 0}</span>
        <span>Qualifying days: {data.qualifying_days || 0}</span>
        <span>Net taxable: {money((data.employment_income || 0) - (data.sed_deduction || 0))}</span>
      </div>
    </Section>

    {/* SA103 — Self-Employment */}
    <Section title="SA103 — Self-Employment" icon={Briefcase} iconColor="text-orange-400" collapsible defaultOpen={false}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <CurrencyInput label="Turnover / Income" value={data.self_employment_income}
          onChange={v => updateField('self_employment_income', v)} testId="self-emp-income" />
        <CurrencyInput label="Allowable Expenses" value={data.self_employment_expenses}
          onChange={v => updateField('self_employment_expenses', v)} testId="self-emp-expenses" />
      </div>
      <p className="text-[10px] text-slate-500 mt-2">
        Net profit: {money((data.self_employment_income || 0) - (data.self_employment_expenses || 0))}
      </p>
    </Section>

    {/* SA105 — Property */}
    <Section title="SA105 — Property Income" icon={Building} iconColor="text-violet-400" collapsible defaultOpen={false}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <CurrencyInput label="Rental Income" value={data.property_income}
          onChange={v => updateField('property_income', v)} testId="property-income" />
        <CurrencyInput label="Property Expenses" value={data.property_expenses}
          onChange={v => updateField('property_expenses', v)} testId="property-expenses" />
      </div>
      <p className="text-[10px] text-slate-500 mt-2">
        Net property income: {money((data.property_income || 0) - (data.property_expenses || 0))}
      </p>
    </Section>

    {/* Dividends & Savings */}
    <Section title="Dividends & Savings" icon={TrendingUp} iconColor="text-teal-400" collapsible defaultOpen={false}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <CurrencyInput label="Dividend Income" value={data.dividend_income}
          onChange={v => updateField('dividend_income', v)} testId="dividend-income" />
        <CurrencyInput label="Savings Interest" value={data.savings_interest}
          onChange={v => updateField('savings_interest', v)} testId="savings-interest" />
      </div>
    </Section>

    {/* Pensions */}
    <Section title="Pensions" icon={PiggyBank} iconColor="text-pink-400" collapsible defaultOpen={false}>
      <CurrencyInput label="Pension Income" value={data.pension_income}
        onChange={v => updateField('pension_income', v)} testId="pension-income" />
    </Section>

    {/* Capital Gains */}
    <Section title="Capital Gains" icon={TrendingUp} iconColor="text-amber-400" collapsible defaultOpen={false}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <CurrencyInput label="Other Gains (shares, crypto, etc.)" value={data.capital_gains}
          onChange={v => updateField('capital_gains', v)} testId="capital-gains" />
        <CurrencyInput label="Residential Property Gains" value={data.capital_gains_residential}
          onChange={v => updateField('capital_gains_residential', v)} testId="cg-residential" />
      </div>
    </Section>

    {/* Other Income (free-form) */}
    <Section title="Other Income" icon={PoundSterling} iconColor="text-emerald-400">
      <div className="space-y-2">
        {(data.other_income || []).map((item, idx) => (
          <div key={item.label ? `${item.label}-${idx}` : `other-income-${idx}`} className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <Label className="text-[10px] text-slate-500">Description</Label>
              <Input value={item.label || ''} onChange={e => updateOtherIncome(idx, 'label', e.target.value)}
                placeholder="e.g. Freelance consulting"
                className="bg-slate-900 border-slate-600 text-white h-8 text-sm"
                data-testid={`other-label-${idx}`} />
            </div>
            <div className="w-32 space-y-1">
              <Label className="text-[10px] text-slate-500">Amount</Label>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs">£</span>
                <Input type="number" step="0.01" min="0" value={item.amount || ''}
                  onChange={e => updateOtherIncome(idx, 'amount', parseFloat(e.target.value) || 0)}
                  className="bg-slate-900 border-slate-600 text-white h-8 text-sm pl-6"
                  data-testid={`other-amount-${idx}`} />
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => removeOtherIncome(idx)}
              className="h-8 px-2 text-red-400 hover:text-red-300 shrink-0" data-testid={`other-remove-${idx}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addOtherIncome}
          className="border-slate-600 text-slate-300 h-8 text-xs" data-testid="add-other-income">
          <Plus className="h-3 w-3 mr-1" />Add Income Source
        </Button>
      </div>
    </Section>

    {/* Tax Already Paid */}
    <Section title="Tax Already Paid" icon={Wallet} iconColor="text-green-400">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <CurrencyInput label="PAYE Tax Deducted" sublabel="Tax deducted by employer"
          value={data.paye_tax_paid} onChange={v => updateField('paye_tax_paid', v)}
          testId="paye-tax-paid" />
        <CurrencyInput label="Tax Deducted at Source" sublabel="E.g. on savings interest"
          value={data.tax_deducted_at_source} onChange={v => updateField('tax_deducted_at_source', v)}
          testId="tax-at-source" />
        <CurrencyInput label="Payments on Account" sublabel="Previous year's advance payments"
          value={data.payments_on_account} onChange={v => updateField('payments_on_account', v)}
          testId="payments-on-account" />
      </div>
    </Section>

    {/* Reliefs */}
    <Section title="Reliefs & Deductions" icon={FileText} iconColor="text-cyan-400" collapsible defaultOpen={false}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <CurrencyInput label="Gift Aid Donations" value={data.gift_aid}
          onChange={v => updateField('gift_aid', v)} testId="gift-aid" />
        <CurrencyInput label="Pension Contributions (personal)" value={data.pension_contributions}
          onChange={v => updateField('pension_contributions', v)} testId="pension-contributions" />
      </div>
    </Section>

    {/* Notes */}
    <Section title="Accountant Notes" icon={StickyNote} iconColor="text-slate-400" collapsible defaultOpen={false}>
      <textarea value={data.accountant_notes || ''} onChange={e => updateField('accountant_notes', e.target.value)}
        rows={3} placeholder="Internal notes about this return..."
        className="w-full bg-slate-900 border border-slate-600 text-white text-sm rounded-md px-3 py-2 resize-none focus:outline-none focus:border-sky-500"
        data-testid="accountant-notes" />
    </Section>
  </>
);

export default IncomeSections;
